import type { EventParticipantRole, EventsManagementSectionAudience } from '../types/events-management'

/** Roles that mean the viewer belongs to the event. Exhibitor members arrive as `exhibitor_member`. */
const EVENT_VIEWER_ROLES: readonly EventParticipantRole[] = ['attendee', 'speaker', 'exhibitor_member']

/** The viewer's known roles, deduplicated in a stable order; unknown roles are dropped. */
export const toViewerRoles = (roles: readonly string[] | undefined): readonly EventParticipantRole[] => {
  const named = new Set((roles ?? []).map(role => role.trim()))
  return EVENT_VIEWER_ROLES.filter(role => named.has(role))
}

/** Registration is the presence of a viewer role. The list `registered` flag is not consulted. */
export const isRegisteredFromRoles = (roles: readonly string[] | undefined): boolean => toViewerRoles(roles).length > 0

/**
 * Attendee App `audience` is a legacy string, `{ type: 'role', role }` or `{ type: 'roles', roles }`. A missing or
 * blank audience keeps the section, so older payloads show every section. Any other audience the client cannot
 * match to a known role hides it.
 */
export const canViewEventSection = (
  audience: EventsManagementSectionAudience | null | undefined,
  viewerRoles: readonly EventParticipantRole[]
): boolean => {
  const type = (typeof audience === 'string' ? audience : audience?.type)?.trim()
  if (!type || type === 'everyone') return true
  if (type === 'participants') return viewerRoles.length > 0
  if (typeof audience !== 'object' || audience === null) return false
  const allowed = type === 'role' ? [audience.role ?? ''] : type === 'roles' ? audience.roles : undefined
  return toViewerRoles(allowed).some(role => viewerRoles.includes(role))
}
